import styled, { css } from 'styled-components';
import articleContentAnchorStyle from 'styles/articleContentAnchorStyles';
import { breakpoint } from 'styles/globals';
import { headers } from 'styles/headers';
import { images } from 'styles/images';
import links from 'styles/links';
import { lists } from 'styles/lists';
import { codeBlocks, paragraphs } from 'styles/paragraphs';
import { blockQuotes, longQuotes, pullQuotes } from 'styles/quotes';
import { tables } from 'styles/tables';
import { getBodyStyle, getColor } from 'styles/theme';
import type { Theme } from 'styles/theme';
import { videos } from 'styles/videos';
import { span } from 'utils/stringUtils';

const gridTemplate = {
  mobile: css`
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    grid-template-areas: 
      "${span('header', 4)}"
      "${span('filter', 4)}"
      "${span('jumpto', 4)}"
      "${span('content', 4)}";
  `,
  tablet: css`
    grid-template-columns: repeat(8, 1fr);
    gap: 48px 32px;
    grid-template-areas: 
      "${span('header', 8)}"
      "${span('filter', 8)}"
      "${span('jumpto', 8)}"
      "${span('content', 8)}"; 
  `,
  smalldesktop: css`
    grid-template-columns: repeat(12, 1fr);
    gap: 68px 24px;
    grid-template-areas: 
      "${span('header', 12)}"
      "${span('filter', 3)} ${span('content', 9)}"
      "${span('jumpto', 3)} ${span('content', 9)}";
  `,
  desktop: css`
    grid-template-columns: repeat(12, 1fr);
    gap: 68px 40px;
    grid-template-areas: 
      "${span('header', 12)}"
      "${span('filter', 3)} ${span('content', 9)}"
      "${span('jumpto', 3)} ${span('content', 9)}";
  `,
};

export const Wrapper = styled.div`
  display: grid;
  grid-template-rows: auto;
  padding-top: 40px;
  margin: auto;
  ${gridTemplate.mobile};

  @media (${breakpoint.mdMin}) {
    ${gridTemplate.tablet};
  }

  @media (${breakpoint.lgMin}) {
    ${gridTemplate.smalldesktop};
    grid-template-rows: auto minmax(50px, auto) 1fr;
  }

  @media (${breakpoint.xlMin}) {
    ${gridTemplate.desktop};
  }

  .jump-to {
    grid-area: jumpto;
    height: 48px;

    @media (${breakpoint.mdMin}) {
      margin-top: -32px;
    }
    
    @media (${breakpoint.lgMin}) {
      margin-top: -52px;
    }
  }
`;

export const Header = styled.div`
  grid-area: header;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (${breakpoint.mdMin}) {
    gap: 24px;
  }

  @media (${breakpoint.lgMin}) {
    gap: 32px;
  }

  @media (${breakpoint.xlMin}) {
    gap: 48px;
  }

`;

export const Content = styled.div<{ $headerHeight: number }>`
  grid-area: content;
  display: flex;
  flex-direction: column;
  gap: 16px;


  @media (${breakpoint.mdMin}) {
    gap: 24px;
  }

  @media (${breakpoint.xlMin}) {
    gap: 40px;
  }

  .sort-and-chips {
    @media (${breakpoint.xlMin}) {
      margin-bottom: -8px;
    }
  }

  .ranking-card {
    scroll-margin-top: ${({ $headerHeight }) => $headerHeight + 56}px;

    @media (${breakpoint.lgMin}) {
      scroll-margin-top: ${({ $headerHeight }) => $headerHeight + 8}px;
    }
  }
`;

export const ContentDescription = styled.div<{ theme: Theme }>`
  ${paragraphs};
  ${headers};
  ${images};
  ${blockQuotes};
  ${pullQuotes};
  ${longQuotes};
  ${videos};
  ${codeBlocks};
  ${tables};
  ${links}
  ${lists};
  ${articleContentAnchorStyle}

  ${getBodyStyle('graphikCompact', { default: 'default' })};
`;

export const Filter = styled(Content)`
  position: relative;
  grid-area: filter;
  height: 100%;
  display: contents;

  @media (${breakpoint.lgMin}) {
    display: flex;
  }

  @media (${breakpoint.xlMin}) {
    gap: 16px;
  }
`;
export const FilterContentWrapper = styled.div`
  grid-area: filter;
  display: flex;
  flex-direction: column;
  gap: 16px;
  .programs-filters {
    overflow: unset;
  }
`;

export const Search = styled.div<{ theme: Theme }>`
  background-color: ${getColor('surfaceWhite')};
  box-shadow: 4px 4px 20px 0px ${getColor('textPrimary')}0D;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const RankingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const ResultText = styled.div<{ theme: Theme }>`
  ${getBodyStyle('graphikCond', { default: 'default' })};
  margin-bottom: 16px;

  &.bold {
    font-weight: bold;
  }
`;
